import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { api } from '../config/api';

interface PossibleLine {
  line_number: number;
  ordered_qty: number;
  unit: string;
  expected_delivery: string | null;
}

interface AmbiguousMatch {
  po_number: string;
  stock_code: string;
  packing_list_qty: number;
  packing_list_unit: string;
  possible_lines: PossibleLine[];
}

interface ImportResult {
  success: boolean;
  status?: string;  // 'requires_line_selection' when duplicates detected
  containers_created?: number;
  container_ids?: number[];
  container_numbers?: string[];
  container_lines_created?: number;
  total_pallets_processed?: number;
  pallets_matched?: number;
  pallets_unmatched?: number;
  po_breakdown?: Array<{
    po_number: string | null;
    line_count: number;
    stock_codes: string[];
    unique_stocks: number;
  }>;
  warning?: string;
  // Line selection fields
  import_id?: number;
  ambiguous_matches?: AmbiguousMatch[];
  supplier?: string;
  po_number?: string;
}

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  result?: ImportResult;
  error?: string;
}

interface PackingListUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;  // Callback to refresh data after successful upload
}

const PackingListUploadModal: React.FC<PackingListUploadModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number | null>(null);

  // Line selection dialog state
  const [showLineSelectionDialog, setShowLineSelectionDialog] = useState(false);
  const [pendingLineSelection, setPendingLineSelection] = useState<{
    importId: number;
    containerIds: number[];
    ambiguousMatches: AmbiguousMatch[];
    fileIndex: number;
    supplier: string;
    poNumber: string;
  } | null>(null);
  const [lineSelections, setLineSelections] = useState<Record<string, number>>({});
  const [isConfirmingSelection, setIsConfirmingSelection] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xml', '.pdf'];

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isProcessing) return;

    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return ACCEPTED_EXTENSIONS.includes(ext);
    });

    if (droppedFiles.length > 0) {
      const newStatuses: FileUploadStatus[] = droppedFiles.map(file => ({
        file,
        status: 'pending',
        progress: 0
      }));
      setFileStatuses(prev => [...prev, ...newStatuses]);
      setSelectedResultIndex(null);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      const newStatuses: FileUploadStatus[] = selectedFiles.map(file => ({
        file,
        status: 'pending',
        progress: 0
      }));
      setFileStatuses(prev => [...prev, ...newStatuses]);
      setSelectedResultIndex(null);
    }
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setFileStatuses(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFileStatuses([]);
    setSelectedResultIndex(null);
  };

  const processAllFiles = async () => {
    const pendingFiles = fileStatuses.filter(fs => fs.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsProcessing(true);

    // Process only pending files
    for (let i = 0; i < fileStatuses.length; i++) {
      const fileStatus = fileStatuses[i];
      if (fileStatus.status !== 'pending') continue;

      // Update status to uploading
      setFileStatuses(prev => prev.map((fs, idx) =>
        idx === i ? { ...fs, status: 'uploading', progress: 10 } : fs
      ));

      try {
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('file', fileStatus.file);
        if (user?.staff_no) {
          formData.append('staff_no', user.staff_no);
        }

        // Update progress
        setFileStatuses(prev => prev.map((fs, idx) =>
          idx === i ? { ...fs, progress: 50 } : fs
        ));

        // Send to API using the v2 endpoint that supports both Excel and XML
        const response = await api.post(
          '/containers/process-packing-list-v2',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 120000
          }
        );

        // Check if line selection is required (duplicate stock codes)
        if (response.data.status === 'requires_line_selection') {
          // Pause processing and show line selection dialog
          setPendingLineSelection({
            importId: response.data.import_id,
            containerIds: response.data.container_ids || [],
            ambiguousMatches: response.data.ambiguous_matches || [],
            fileIndex: i,
            supplier: response.data.supplier || '',
            poNumber: response.data.po_number || ''
          });
          setLineSelections({});
          setShowLineSelectionDialog(true);
          setIsProcessing(false);
          return; // Stop processing - will resume after user confirms selection
        }

        // Update status to success
        setFileStatuses(prev => prev.map((fs, idx) =>
          idx === i ? { ...fs, status: 'success', progress: 100, result: response.data } : fs
        ));

      } catch (err: any) {
        console.error(`Error processing ${fileStatus.file.name}:`, err);

        const errorData = err.response?.data;
        let errorMessage = 'Failed to process packing list';

        if (errorData) {
          if (errorData.error_type === 'missing_po') {
            errorMessage = `PO Number Missing: ${errorData.error}`;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Update status to error
        setFileStatuses(prev => prev.map((fs, idx) =>
          idx === i ? { ...fs, status: 'error', progress: 100, error: errorMessage } : fs
        ));
      }
    }

    setIsProcessing(false);

    // Trigger refresh callback if any files succeeded
    const hasSuccess = fileStatuses.some(fs => fs.status === 'success') ||
                       fileStatuses.some(fs => fs.status === 'pending'); // Will become success
    if (hasSuccess && onSuccess) {
      onSuccess();
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setFileStatuses([]);
      setSelectedResultIndex(null);
      onClose();
    }
  };

  // Line selection handlers
  const handleLineSelect = (stockCode: string, lineNumber: number) => {
    setLineSelections(prev => ({
      ...prev,
      [stockCode]: lineNumber
    }));
  };

  const handleCancelLineSelection = () => {
    if (!pendingLineSelection) return;

    // Mark the file as error since user cancelled
    setFileStatuses(prev => prev.map((fs, idx) =>
      idx === pendingLineSelection.fileIndex
        ? { ...fs, status: 'error', progress: 100, error: 'Line selection cancelled' }
        : fs
    ));

    setShowLineSelectionDialog(false);
    setPendingLineSelection(null);
    setLineSelections({});
  };

  const handleConfirmLineSelection = async () => {
    if (!pendingLineSelection) return;

    // Check all selections are made
    const allSelected = pendingLineSelection.ambiguousMatches.every(
      match => lineSelections[match.stock_code] !== undefined
    );
    if (!allSelected) return;

    setIsConfirmingSelection(true);

    try {
      // Build selections array
      const selections = pendingLineSelection.ambiguousMatches.map(match => ({
        po_number: match.po_number,
        stock_code: match.stock_code,
        line_number: lineSelections[match.stock_code]
      }));

      const response = await api.post('/containers/confirm-line-selection', {
        import_id: pendingLineSelection.importId,
        container_ids: pendingLineSelection.containerIds,
        selections
      });

      // Update file status to success
      setFileStatuses(prev => prev.map((fs, idx) =>
        idx === pendingLineSelection.fileIndex
          ? {
              ...fs,
              status: 'success',
              progress: 100,
              result: {
                success: true,
                container_ids: response.data.container_ids,
                container_lines_created: response.data.container_lines_created,
                po_number: response.data.po_number
              }
            }
          : fs
      ));

      setShowLineSelectionDialog(false);
      setPendingLineSelection(null);
      setLineSelections({});

      // Trigger refresh
      if (onSuccess) {
        onSuccess();
      }

    } catch (err: any) {
      console.error('Error confirming line selection:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to confirm line selection';

      setFileStatuses(prev => prev.map((fs, idx) =>
        idx === pendingLineSelection.fileIndex
          ? { ...fs, status: 'error', progress: 100, error: errorMessage }
          : fs
      ));

      setShowLineSelectionDialog(false);
      setPendingLineSelection(null);
      setLineSelections({});
    } finally {
      setIsConfirmingSelection(false);
    }
  };

  const allSelectionsComplete = pendingLineSelection
    ? pendingLineSelection.ambiguousMatches.every(match => lineSelections[match.stock_code] !== undefined)
    : false;

  const getStatusIcon = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'pending': return <PendingIcon color="disabled" />;
      case 'uploading': return <CircularProgress size={24} />;
      case 'success': return <SuccessIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'pending': return 'grey.400';
      case 'uploading': return 'primary.main';
      case 'success': return 'success.main';
      case 'error': return 'error.main';
    }
  };

  const successCount = fileStatuses.filter(fs => fs.status === 'success').length;
  const errorCount = fileStatuses.filter(fs => fs.status === 'error').length;
  const pendingCount = fileStatuses.filter(fs => fs.status === 'pending').length;
  const totalContainersCreated = fileStatuses
    .filter(fs => fs.result?.containers_created)
    .reduce((sum, fs) => sum + (fs.result?.containers_created || 0), 0);

  const selectedResult = selectedResultIndex !== null ? fileStatuses[selectedResultIndex]?.result : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Upload Packing Lists</Typography>
          <IconButton onClick={handleClose} disabled={isProcessing}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* File Selection Area */}
        <Box sx={{ mb: 3 }}>
          <input
            accept=".xlsx,.xls,.xlsm,.xml,.pdf"
            style={{ display: 'none' }}
            id="modal-packing-list-upload"
            type="file"
            multiple
            onChange={handleFileSelect}
            disabled={isProcessing}
          />
          <label htmlFor="modal-packing-list-upload">
            <Box
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: isProcessing ? 'default' : 'pointer',
                backgroundColor: isDragOver ? 'primary.50' : 'transparent',
                transition: 'border-color 0.2s, background-color 0.2s',
                opacity: isProcessing ? 0.5 : 1,
                '&:hover': !isProcessing ? {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                } : {}
              }}
            >
              <UploadIcon sx={{ fontSize: 40, color: isDragOver ? 'primary.main' : 'grey.500', mb: 1 }} />
              <Typography variant="body1" fontWeight="medium">
                Drag & drop packing list files here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Supports: .xlsx, .xls, .xlsm, .xml, .pdf
              </Typography>
            </Box>
          </label>
        </Box>

        {/* File List */}
        {fileStatuses.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Files ({fileStatuses.length})
              </Typography>
              {!isProcessing && (
                <Button size="small" onClick={clearAll} startIcon={<RefreshIcon />}>
                  Clear All
                </Button>
              )}
            </Box>

            <List sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
              {fileStatuses.map((fs, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderLeft: '4px solid',
                    borderLeftColor: getStatusColor(fs.status),
                    mb: 0.5,
                    bgcolor: 'background.paper',
                    cursor: fs.status === 'success' ? 'pointer' : 'default',
                    '&:hover': fs.status === 'success' ? { bgcolor: 'action.hover' } : {}
                  }}
                  onClick={() => fs.status === 'success' && setSelectedResultIndex(index)}
                  secondaryAction={
                    fs.status === 'pending' && !isProcessing ? (
                      <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); removeFile(index); }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getStatusIcon(fs.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <FileIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {fs.file.name}
                        </Typography>
                        {fs.result?.containers_created !== undefined && (
                          <Chip
                            label={`${fs.result.containers_created} container${fs.result.containers_created !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                          />
                        )}
                        {fs.result?.pallets_matched !== undefined && (
                          <Chip
                            label={`${fs.result.pallets_matched}/${fs.result.total_pallets_processed} matched`}
                            size="small"
                            color="success"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      fs.status === 'error' ? (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {fs.error}
                        </Typography>
                      ) : fs.status === 'uploading' ? (
                        <LinearProgress variant="determinate" value={fs.progress} sx={{ mt: 1 }} />
                      ) : fs.status === 'success' ? (
                        <Typography variant="caption" color="success.main">
                          Click to view details
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {(fs.file.size / 1024).toFixed(1)} KB
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Summary Stats */}
            {(successCount > 0 || errorCount > 0) && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {successCount > 0 && (
                  <Chip
                    icon={<SuccessIcon />}
                    label={`${successCount} successful`}
                    color="success"
                    size="small"
                  />
                )}
                {errorCount > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${errorCount} failed`}
                    color="error"
                    size="small"
                  />
                )}
                {totalContainersCreated > 0 && (
                  <Chip
                    label={`${totalContainersCreated} containers created`}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            )}
          </>
        )}

        {/* Selected Result Details */}
        {selectedResult && selectedResult.success && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Details: {fileStatuses[selectedResultIndex!]?.file.name}
                </Typography>
                <IconButton size="small" onClick={() => setSelectedResultIndex(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {selectedResult.warning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {selectedResult.warning}
                </Alert>
              )}

              {/* PO Detection Breakdown */}
              {selectedResult.po_breakdown && selectedResult.po_breakdown.length > 0 && (
                <Box>
                  <Alert
                    severity={selectedResult.po_breakdown.length > 1 ? "info" : "success"}
                    sx={{ mb: 2 }}
                  >
                    {selectedResult.po_breakdown.length === 1 && selectedResult.po_breakdown[0].po_number ? (
                      `All lines matched to PO ${selectedResult.po_breakdown[0].po_number}`
                    ) : (
                      `Multi-PO: Lines matched to ${selectedResult.po_breakdown.filter(po => po.po_number !== null).length} different POs`
                    )}
                  </Alert>

                  {selectedResult.po_breakdown.map((po, idx) => (
                    <Box
                      key={po.po_number || 'unmatched'}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        border: '1px solid',
                        borderColor: po.po_number ? 'grey.300' : 'warning.main',
                        borderRadius: 1,
                        bgcolor: po.po_number ? 'grey.50' : 'warning.light'
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {po.po_number ? `PO ${po.po_number}` : 'Unmatched'}
                          {idx === 0 && po.po_number && selectedResult.po_breakdown!.length > 1 && (
                            <Chip label="Primary" size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Chip
                          label={`${po.line_count} lines`}
                          size="small"
                          color={po.po_number ? "default" : "warning"}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {po.stock_codes.slice(0, 4).map((code) => (
                          <Chip key={code} label={code} size="small" variant="outlined" />
                        ))}
                        {po.stock_codes.length > 4 && (
                          <Chip label={`+${po.stock_codes.length - 4} more`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Container Numbers */}
              {selectedResult.container_numbers && selectedResult.container_numbers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Containers:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedResult.container_numbers.map((no) => (
                      <Chip key={no} label={no.trim()} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isProcessing}>
          {successCount > 0 || errorCount > 0 ? 'Done' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={processAllFiles}
          disabled={pendingCount === 0 || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
        >
          {isProcessing ? 'Processing...' : `Process ${pendingCount > 0 ? pendingCount : ''} File${pendingCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>

      {/* Line Selection Dialog - for duplicate stock codes */}
      <Dialog
        open={showLineSelectionDialog}
        maxWidth="sm"
        fullWidth
        onClose={() => {}} // Prevent closing by clicking outside
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6">Confirm PO Line</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            This stock code appears on multiple PO lines. Please select which line this shipment belongs to.
          </Alert>

          {pendingLineSelection && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Supplier: <strong>{pendingLineSelection.supplier}</strong> &nbsp;|&nbsp;
                PO: <strong>{pendingLineSelection.poNumber}</strong>
              </Typography>
            </Box>
          )}

          {pendingLineSelection?.ambiguousMatches.map((match) => (
            <Card
              key={`${match.po_number}-${match.stock_code}`}
              sx={{ mb: 2, border: '1px solid', borderColor: 'grey.300' }}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {match.stock_code}
                </Typography>
                <Typography variant="body2" color="primary.main" sx={{ mb: 2 }}>
                  Packing list: <strong>{match.packing_list_qty.toLocaleString()} {match.packing_list_unit}</strong>
                </Typography>

                <RadioGroup
                  value={lineSelections[match.stock_code] || ''}
                  onChange={(e) => handleLineSelect(match.stock_code, parseInt(e.target.value))}
                >
                  {match.possible_lines.map((line) => (
                    <FormControlLabel
                      key={line.line_number}
                      value={line.line_number}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">
                            <strong>Line {line.line_number}:</strong> {line.ordered_qty.toLocaleString()} {line.unit}
                          </Typography>
                          {line.expected_delivery && (
                            <Typography variant="body2" color="text.secondary">
                              Due: {new Date(line.expected_delivery).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{
                        alignItems: 'flex-start',
                        py: 1,
                        px: 1,
                        mx: 0,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                        ...(lineSelections[match.stock_code] === line.line_number && {
                          bgcolor: 'primary.light',
                          '&:hover': { bgcolor: 'primary.light' }
                        })
                      }}
                    />
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCancelLineSelection}
            disabled={isConfirmingSelection}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmLineSelection}
            disabled={!allSelectionsComplete || isConfirmingSelection}
            startIcon={isConfirmingSelection ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isConfirmingSelection ? 'Confirming...' : 'Confirm Selection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PackingListUploadModal;
